import React, { useContext, useEffect } from 'react'
import InputText from '@Components/Fields/InputText'
import NewUserPermissionGroup from '@Components/AddNewUserSub/AddNewUserStep/NewUserPermissionGroup'
import Button from '@Components/Buttons/Button'
import { emailIsValid, valueIsEmpty } from '@App/helpers/validate'
import { IMyUserGroups } from '@Components/AddNewUserSub'
import { PERMISSION_NAMES } from '@App/enum/user-permissions'
import { StoresContext } from '@App/contexts'
import { observer } from 'mobx-react'
import { UserGroup } from '@Entities/user/user'

interface AddNewUserStepProps {
    changeFormTo: (type: 'ADD' | 'CONFIRMED') => void
}

const AddNewUserStep = ({ changeFormTo }: AddNewUserStepProps) => {
    const { userStore, preloaderStore, rightsholderUserStore, popupStore } = useContext(StoresContext)

    const [firstName, setFirstName] = React.useState<string>('')
    const [lastName, setLastName] = React.useState<string>('')
    const [email, setEmail] = React.useState<string>('')
    const [myUserGroups, setMyUserGroups] = React.useState<IMyUserGroups[]>([])
    const [groupChecked, setGroupChecked] = React.useState<boolean>(false)

    const [firstNameError, setFirstNameError] = React.useState<string>('')
    const [lastNameError, setLastNameError] = React.useState<string>('')
    const [emailError, setEmailError] = React.useState<string>('')

    const handleValueChange = (
        setter: React.Dispatch<React.SetStateAction<string>>,
        errorSetter: React.Dispatch<React.SetStateAction<string>>,
        maxLength: number,
        value: string
    ) => {
        if (!valueIsEmpty(value)) {
            setter(value)
        } else {
            setter('')
        }
        if (value && value.length > maxLength) {
            errorSetter(`Invalid value, max length ${maxLength}`)
        } else {
            errorSetter('')
        }
    }

    const onChangeFirstName = (value: string) => {
        handleValueChange(setFirstName, setFirstNameError, 60, value)
    }

    const onChangeLastName = (value: string) => {
        handleValueChange(setLastName, setLastNameError, 60, value)
    }

    const onChangeEmail = (value: string) => {
        handleValueChange(setEmail, setEmailError, 2000, value)
        // Validate and store trimmed email to avoid validation issues caused by leading/trailing spaces
        const trimmed = value ? value.trim() : ''
        if (trimmed && !emailIsValid(trimmed)) {
            setEmailError('Please enter a valid email address')
        } else {
            setEmailError('')
        }
    }

    const superUserChecked = (superUserIsChecked: boolean) => {
        setMyUserGroups(
            myUserGroups.map((item: IMyUserGroups) => {
                const otherItemsChecked = superUserIsChecked ? false : item.checked

                return {
                    ...item,
                    checked: item.name === PERMISSION_NAMES.super_user ? superUserIsChecked : otherItemsChecked,
                    disabled: item.name !== PERMISSION_NAMES.super_user && superUserIsChecked,
                }
            })
        )
    }

    const onCheckGroup = (index: number) => {
        setMyUserGroups((prev) => {
            const newGroups = prev.map((item, i) => (i === index ? { ...item, checked: !item.checked } : item))
            setGroupChecked(newGroups.some((item: IMyUserGroups) => item.checked))

            const superUserIsChecked =
                newGroups[index]?.name === PERMISSION_NAMES.super_user && newGroups[index]?.checked
            superUserChecked(!!superUserIsChecked)

            return newGroups
        })
    }

    const onInvite = async () => {
        // Use trimmed email for validation and sending
        const trimmedEmail = email ? email.trim() : ''
        const emailErrorMessage =
            trimmedEmail && !emailIsValid(trimmedEmail) ? 'Please enter a valid email address' : ''
        setEmailError(emailErrorMessage)

        if (!emailErrorMessage) {
            const groups = myUserGroups
                .filter((item: IMyUserGroups) => item.checked)
                .map((item: IMyUserGroups) => ({
                    name: item.name,
                }))
            const formData: any = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: trimmedEmail,
                groups: groups,
            }

            try {
                preloaderStore.openTransparentPreloader(true)
                await rightsholderUserStore.inviteRightsholderUser(formData)
                await rightsholderUserStore.loadRightsholderUsers({
                    page: 1,
                    limit: 10,
                })
                preloaderStore.openTransparentPreloader(false)
                popupStore.setAlertData('Invite sent!', false)
                changeFormTo('CONFIRMED')
            } catch (error) {
                popupStore.setAlertFromError(error)
                preloaderStore.openTransparentPreloader(false)
            }
        }
    }

    const setUsersGroup = () => {
        const userGroupsCollection = userStore.permissionGroups

        if (userGroupsCollection) {
            setMyUserGroups(
                userGroupsCollection.map((item: UserGroup) => ({
                    ...item,
                    checked: false,
                    disabled: false,
                }))
            )
        }
    }

    useEffect(() => {
        setUsersGroup()
    }, [])

    const isInternal = userStore.isInternal
    const formIsReady =
        !!firstName && !!lastName && !!email && !emailError && !firstNameError && !lastNameError && groupChecked

    return (
        <div className="add-new-user-sub-form-component">
            <h2>Add New User</h2>
            <div className="inputs-field">
                <InputText
                    mainColor
                    id="first-name"
                    value={firstName}
                    label="First Name"
                    assistiveText="Required"
                    errorMessage={firstNameError}
                    onChange={onChangeFirstName}
                />
                <InputText
                    mainColor
                    id="last-name"
                    value={lastName}
                    label="Last Name"
                    assistiveText="Required"
                    errorMessage={lastNameError}
                    onChange={onChangeLastName}
                />
                <InputText
                    mainColor
                    id="email"
                    value={email}
                    label="Email Address"
                    assistiveText="Required"
                    errorMessage={emailError}
                    onChange={onChangeEmail}
                />
            </div>
            <br />
            <h5>User permission</h5>
            <NewUserPermissionGroup myUserGroups={myUserGroups} onCheckGroup={onCheckGroup} />

            <Button mainColor type="primary" disabled={!formIsReady || isInternal} onClick={onInvite}>
                Invite
            </Button>
        </div>
    )
}

export default observer(AddNewUserStep)
